(() => {
  if (customElements.get('bn-countdown')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pad = (n) => String(n).padStart(2, '0');

  // One clock for all countdowns on the page.
  const subscribers = new Set();
  let clock = null;

  function subscribe(fn) {
    subscribers.add(fn);
    fn(Date.now());
    if (!clock) clock = setInterval(() => subscribers.forEach((f) => f(Date.now())), 1000);
  }

  function unsubscribe(fn) {
    subscribers.delete(fn);
    if (!subscribers.size) {
      clearInterval(clock);
      clock = null;
    }
  }

  function readPhases(el) {
    const tz = el.dataset.tz || 'Z';
    return JSON.parse(el.dataset.phases).map((phase) => ({
      ...phase,
      start: Date.parse(phase.start + tz),
      end: Date.parse(phase.end + tz),
    }));
  }

  function split(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    return {
      d: Math.floor(total / 86400),
      h: Math.floor((total % 86400) / 3600),
      m: Math.floor((total % 3600) / 60),
      s: total % 60,
    };
  }

  const clamp01 = (n) => Math.min(1, Math.max(0, n));

  // Rolling digits: the new digit enters from above and the old one exits below.
  class RollingNumber {
    constructor(slot) {
      slot.textContent = '';
      this.cells = [0, 1].map(() => {
        const cell = document.createElement('span');
        cell.className = 'bn-digit';
        const value = document.createElement('span');
        value.className = 'bn-digit__value';
        cell.append(value);
        slot.append(cell);
        return cell;
      });
    }

    set(text) {
      [...text].forEach((char, i) => {
        const cell = this.cells[i];
        const current = cell.lastElementChild;
        if (current.textContent === char) return;

        if (current.textContent === '' || reducedMotion.matches) {
          current.textContent = char;
          return;
        }

        const next = document.createElement('span');
        next.className = 'bn-digit__value bn-digit__value--in';
        next.textContent = char;
        current.classList.add('bn-digit__value--out');
        cell.append(next);
        setTimeout(() => current.remove(), 450);
      });
    }
  }

  class BnCountdown extends HTMLElement {
    connectedCallback() {
      this.phases = readPhases(this);
      this.labelEl = this.querySelector('[data-bn-label]');
      this.meterEl = this.querySelector('[data-bn-meter]');
      this.nextEl = this.querySelector('[data-bn-next]');
      this.numbers = {};
      this.querySelectorAll('[data-unit]').forEach((slot) => {
        this.numbers[slot.dataset.unit] = new RollingNumber(slot);
      });
      this.phaseIndex = -1;
      this.tick = (now) => this.update(now);
      subscribe(this.tick);
    }

    disconnectedCallback() {
      unsubscribe(this.tick);
    }

    update(now) {
      const index = this.phases.findIndex((p) => now < p.end);

      if (index === -1) {
        this.hidden = true;
        unsubscribe(this.tick);
        return;
      }

      const phase = this.phases[index];

      if (index !== this.phaseIndex) {
        this.phaseIndex = index;
        if (this.labelEl) this.labelEl.textContent = phase.label;
        if (this.nextEl) {
          const next = this.phases[index + 1];
          this.nextEl.textContent = next ? `Next drop: ${next.name} · ${next.date}` : 'Final drop of Black November';
        }
      }

      const left = split(phase.end - now);
      Object.entries(this.numbers).forEach(([unit, number]) => number.set(pad(left[unit])));

      if (this.meterEl) {
        const remaining = clamp01((phase.end - now) / (phase.end - phase.start));
        this.meterEl.style.transform = `scaleX(${remaining})`;
      }

      this.hidden = false;
    }
  }

  class BnTimeline extends HTMLElement {
    connectedCallback() {
      this.phases = readPhases(this);
      this.render();
      this.tick = (now) => this.update(now);
      subscribe(this.tick);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            this.classList.add('is-visible');
            this.observer.disconnect();
          },
          { threshold: 0.35 }
        );
        this.observer.observe(this);
      } else {
        this.classList.add('is-visible');
      }
    }

    disconnectedCallback() {
      unsubscribe(this.tick);
      if (this.observer) this.observer.disconnect();
    }

    render() {
      this.textContent = '';
      this.segments = this.phases.map((phase, i) => {
        const seg = document.createElement('div');
        seg.className = 'bn-tl__seg';
        seg.style.setProperty('--bn-delay', `${i * 0.25}s`);
        seg.innerHTML = `
          <span class="bn-tl__step">0${i + 1}</span>
          <span class="bn-tl__name"></span>
          <span class="bn-tl__date"></span>
          <span class="bn-tl__bar"><span class="bn-tl__fill"></span><span class="bn-tl__dot"></span></span>
          <span class="bn-tl__status"></span>`;
        seg.querySelector('.bn-tl__name').textContent = phase.name;
        seg.querySelector('.bn-tl__date').textContent = phase.date;
        this.append(seg);
        return seg;
      });
    }

    update(now) {
      this.phases.forEach((phase, i) => {
        const seg = this.segments[i];
        const status = seg.querySelector('.bn-tl__status');
        const state = now >= phase.end ? 'ended' : now >= phase.start ? 'live' : 'upcoming';

        seg.dataset.state = state;
        seg.style.setProperty('--bn-fill', clamp01((now - phase.start) / (phase.end - phase.start)));

        if (state === 'ended') {
          status.textContent = 'Ended';
        } else if (state === 'live') {
          const left = split(phase.end - now);
          status.textContent = `Live · ends in ${left.d > 0 ? `${left.d}d ` : ''}${pad(left.h)}h ${pad(left.m)}m ${pad(left.s)}s`;
        } else {
          status.textContent = 'Up next';
        }
      });
    }
  }

  customElements.define('bn-countdown', BnCountdown);
  customElements.define('bn-timeline', BnTimeline);
})();
